import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, Globe, MessageSquareText, Shield, ArrowRight, Sparkles, Zap } from 'lucide-react';

const LandingPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCTA = () => {
    if (user) navigate('/dashboard');
    else navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-inter overflow-hidden">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-black/60 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center">
              <Video className="w-4 h-4 text-black" />
            </div>
            <span className="font-satoshi font-bold text-lg tracking-tight">Kaplie</span>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <button onClick={() => navigate('/dashboard')} data-testid="nav-dashboard" className="bg-white text-black font-medium rounded-full px-5 py-2 text-sm hover:bg-zinc-200 transition-colors">
                Dashboard
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} data-testid="nav-login" className="text-zinc-400 hover:text-white text-sm font-medium transition-colors px-4 py-2">
                  Sign in
                </button>
                <button onClick={() => navigate('/signup')} data-testid="nav-signup" className="bg-white text-black font-medium rounded-full px-5 py-2 text-sm hover:bg-zinc-200 transition-colors">
                  Get Started
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        {/* Ambient glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#D4AF37]/[0.04] rounded-full blur-[150px]" />
          <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-violet-500/[0.03] rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-blue-500/[0.03] rounded-full blur-[130px]" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/[0.08] rounded-full px-4 py-1.5 mb-8 animate-fade-in">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="text-xs font-medium text-zinc-400 tracking-wide">Next-gen video communication</span>
          </div>

          {/* Headline */}
          <h1 className="font-satoshi font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter leading-[0.9] mb-6">
            <span className="text-white">Video calls</span>
            <br />
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5E6C8] bg-clip-text text-transparent">you understand</span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed font-inter">
            Live captions appear instantly as the other person speaks. Translate to 10 languages. Save every conversation with AI summaries.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <button 
              onClick={handleCTA}
              data-testid="hero-cta"
              className="group bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-semibold rounded-full px-8 py-3.5 text-base hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] transition-all duration-300"
            >
              Start free trial
              <ArrowRight className="w-4 h-4 inline-block ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
            <span className="text-sm text-zinc-500">3 days free · No credit card to try</span>
          </div>

          {/* Hero device mockup */}
          <div className="relative max-w-3xl mx-auto" style={{ transform: `translateY(${scrollY * 0.05}px)` }}>
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent z-10 pointer-events-none" />
            <div className="rounded-2xl overflow-hidden border border-white/[0.08] shadow-[0_0_80px_rgba(212,175,55,0.08)]">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/aa241646-b620-4163-898e-a59229c84660/images/a0b630f37975e3570eccfe77365fe76b99fa04453afea160b83935bd97410e92.png" 
                alt="Kaplie video call with live captions" 
                className="w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]/80 mb-4">Features</p>
            <h2 className="font-satoshi font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Everything you need.
              <br />
              <span className="text-zinc-500">Nothing you don't.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {/* Large: Live Captions */}
            <div className="md:col-span-7 group p-8 md:p-10 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-[#D4AF37]/20 hover:shadow-[0_0_40px_rgba(212,175,55,0.05)] transition-all duration-500">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                <MessageSquareText className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-satoshi font-bold text-2xl mb-3 tracking-tight">Live Captions</h3>
              <p className="text-zinc-400 leading-relaxed max-w-md">
                Words appear in real-time as the other person speaks. Powered by native browser speech recognition for zero latency.
              </p>
            </div>

            {/* Translation */}
            <div className="md:col-span-5 group p-8 md:p-10 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-violet-500/20 hover:shadow-[0_0_40px_rgba(138,43,226,0.05)] transition-all duration-500">
              <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mb-6">
                <Globe className="w-6 h-6 text-violet-400" />
              </div>
              <h3 className="font-satoshi font-bold text-2xl mb-3 tracking-tight">10 Languages</h3>
              <p className="text-zinc-400 leading-relaxed">
                Real-time translation. Speak English, they read Spanish. Powered by AI.
              </p>
            </div>

            {/* Save conversations */}
            <div className="md:col-span-5 group p-8 md:p-10 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-blue-500/20 hover:shadow-[0_0_40px_rgba(0,229,255,0.05)] transition-all duration-500">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6">
                <Sparkles className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-satoshi font-bold text-2xl mb-3 tracking-tight">AI Summaries</h3>
              <p className="text-zinc-400 leading-relaxed">
                Save any conversation. Get a full transcript and AI-generated summary of key points.
              </p>
            </div>

            {/* Share links */}
            <div className="md:col-span-7 group p-8 md:p-10 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-[#D4AF37]/20 hover:shadow-[0_0_40px_rgba(212,175,55,0.05)] transition-all duration-500">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-satoshi font-bold text-2xl mb-3 tracking-tight">One-tap calling</h3>
              <p className="text-zinc-400 leading-relaxed max-w-md">
                Share a link. Anyone joins instantly — no download, no account needed. Works on any device with a browser.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]/80 mb-4">Pricing</p>
            <h2 className="font-satoshi font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-zinc-400 text-lg">Only the host pays. Guests always join free.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Basic */}
            <div className="p-8 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12] transition-all duration-300">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-zinc-400" />
                <span className="font-satoshi font-bold text-lg">Basic</span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-satoshi font-black text-4xl">$9.99</span>
                <span className="text-zinc-500 text-sm">/month</span>
              </div>
              <p className="text-sm text-zinc-500 mb-6">1 guest per call</p>
              <button onClick={handleCTA} className="w-full py-3 bg-white/[0.06] hover:bg-white/[0.1] rounded-full font-medium text-sm transition-colors">
                Start free trial
              </button>
            </div>

            {/* Pro */}
            <div className="relative p-8 rounded-2xl bg-gradient-to-b from-[#D4AF37]/[0.06] to-transparent border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all duration-300">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider">Popular</div>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                <span className="font-satoshi font-bold text-lg">Pro</span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-satoshi font-black text-4xl bg-gradient-to-r from-[#D4AF37] to-[#F5E6C8] bg-clip-text text-transparent">$17.99</span>
                <span className="text-zinc-500 text-sm">/month</span>
              </div>
              <p className="text-sm text-zinc-500 mb-6">Up to 5 guests per call</p>
              <button onClick={handleCTA} className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-semibold rounded-full text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all">
                Start free trial
              </button>
            </div>
          </div>

          <p className="text-center text-zinc-600 text-sm mt-8">3-day free trial on all plans. Cancel anytime.</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#D4AF37]/[0.03] rounded-full blur-[150px]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-satoshi font-black text-4xl sm:text-5xl tracking-tighter mb-6">
            Ready to be understood?
          </h2>
          <p className="text-zinc-400 text-lg mb-8">Join Kaplie and never miss a word again.</p>
          <button onClick={handleCTA} data-testid="final-cta" className="group bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-semibold rounded-full px-8 py-3.5 text-base hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] transition-all duration-300">
            Get started free
            <ArrowRight className="w-4 h-4 inline-block ml-2 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center">
              <Video className="w-3 h-3 text-black" />
            </div>
            <span className="font-satoshi font-bold text-sm">Kaplie</span>
          </div>
          <p className="text-zinc-600 text-xs">&copy; {new Date().getFullYear()} Kaplie. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
